import React from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, X, Heart, LogOut, User, Zap } from 'lucide-react';

interface NavbarProps {
  searchQuery: string;
  setSearchQuery: (val: string) => void;
  minPrice: number | '';
  setMinPrice: (val: number | '') => void;
  maxPrice: number | '';
  setMaxPrice: (val: number | '') => void;
  view: 'home' | 'saved';
  setView: (val: 'home' | 'saved') => void;
  user: any;
  userData: any;
  isAdmin: boolean;
  handleGoogleLogin: () => void;
  handleLogout: () => void;
  onPostClick: () => void;
  setShowFilters: (val: boolean) => void;
  activeBrand: string;
  onlyHighDiscount: boolean;
}

export default function Navbar({
  searchQuery, setSearchQuery, minPrice, setMinPrice, maxPrice, setMaxPrice,
  view, setView, user, userData, isAdmin, handleGoogleLogin, handleLogout,
  onPostClick, setShowFilters, activeBrand, onlyHighDiscount
}: NavbarProps) {
  return (
    <div className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm transition-all duration-200">
      
      {/* TOP ROW: Logo, Desktop Search, and Action Buttons */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        
        {/* 1. Logo (Scales slightly smaller on mobile to prevent layout pushing) */}
        <Link to="/" onClick={() => setView('home')} className="flex items-center gap-3 flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity">
          <img src="/ev-logo.svg" alt="EV Upgrades" className="h-10 md:h-16 w-auto object-contain" />
        </Link>
        
        {/* 2. Desktop Search & Price (Hidden on Mobile) */}
        <div className="hidden md:flex items-center gap-3 flex-1 max-w-3xl mx-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input type="text" placeholder="Search accessories, upgrades..." className="w-full pl-10 pr-10 py-2.5 bg-gray-100 rounded-full focus:ring-2 focus:ring-blue-500 outline-none transition-all" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>}
          </div>

          <div className="flex items-center gap-2">
            <div className="relative w-24">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-bold">$</span>
              <input type="number" placeholder="Min" className="w-full pl-7 pr-2 py-2.5 bg-gray-100 rounded-full focus:ring-2 focus:ring-blue-500 outline-none transition-all text-sm" value={minPrice} onChange={(e) => setMinPrice(e.target.value ? Number(e.target.value) : '')} />
            </div>
            <span className="text-gray-400 font-bold">-</span>
            <div className="relative w-24">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-bold">$</span>
              <input type="number" placeholder="Max" className="w-full pl-7 pr-2 py-2.5 bg-gray-100 rounded-full focus:ring-2 focus:ring-blue-500 outline-none transition-all text-sm" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value ? Number(e.target.value) : '')} />
            </div>
          </div>
        </div>

        {/* 3. Action Buttons (Forced to the front with relative z-50 to guarantee clicks) */}
        <div className="flex items-center gap-2 relative z-50">
            <button onClick={() => setView(view === 'saved' ? 'home' : 'saved')} className={`p-2 rounded-full relative transition-colors ${view === 'saved' ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-red-500'}`} title="My Saved Upgrades">
              <Heart className={`w-5 h-5 ${view === 'saved' ? 'fill-current' : ''}`} />
            </button>
            
            {user ? (
               <div className="flex items-center gap-3 pl-2 border-l border-gray-200 ml-2">
                   {isAdmin && (
                       <button onClick={onPostClick} className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-1.5 rounded-full text-sm font-bold shadow-sm flex items-center gap-2 transition-colors">
                           <Zap className="w-4 h-4" /> Post
                       </button>
                   )}
                   <div className="flex items-center bg-white rounded-full pr-3 border border-gray-200 shadow-sm hover:bg-gray-50 transition-colors cursor-pointer">
                       <img src={user.photoURL || `https://ui-avatars.com/api/?name=${user.email}`} className="w-8 h-8 rounded-full border-r border-gray-200" alt="Avatar" />
                       <span className="ml-2 text-xs font-extrabold text-blue-600 flex items-center gap-1" title="My Lifetime Reputation">
                           🏆 {userData?.reputation || 0}
                       </span>
                   </div>
                   <button onClick={handleLogout} className="text-gray-400 hover:text-red-500 p-1" title="Sign Out"><LogOut className="w-4 h-4" /></button>
               </div>
            ) : (
               <button onClick={handleGoogleLogin} className="flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-full text-sm font-bold hover:bg-slate-800 transition-colors ml-1 sm:ml-2 shadow-sm"><User className="w-4 h-4" /> <span className="hidden sm:inline">Sign In</span></button>
            )}
            
            {/* Mobile Filter Button */}
            <button onClick={() => setShowFilters(true)} className="md:hidden p-2 bg-gray-100 rounded-full hover:bg-gray-200 relative"><Filter className="w-5 h-5" />{(minPrice || maxPrice || activeBrand !== 'All Brands' || onlyHighDiscount) && <div className="absolute top-0 right-0 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white" />}</button>
        </div>
      </div>

      {/* BOTTOM ROW: Mobile Search Bar (Only visible on phones) */}
      <div className="md:hidden px-4 pb-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input type="text" placeholder="Search accessories..." className="w-full pl-10 pr-10 py-2.5 bg-gray-100 rounded-full focus:ring-2 focus:ring-blue-500 outline-none transition-all text-sm" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
          {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>}
        </div>
      </div>

    </div>
  );
}